import { useEffect, useMemo, useReducer } from 'react';
import { SignDesignDocumentV2, SignLayer } from './types';

export interface EditorState {
  past: SignDesignDocumentV2[];
  present: SignDesignDocumentV2;
  future: SignDesignDocumentV2[];
}

export type EditorAction =
  | { type: 'add'; layer: SignLayer }
  | { type: 'remove'; id: string }
  | { type: 'duplicate'; id: string; newId: string }
  | { type: 'rename'; id: string; name: string }
  | { type: 'reorder'; id: string; index: number }
  | { type: 'group'; ids: string[]; groupId: string }
  | { type: 'ungroup'; groupId: string }
  | { type: 'toggleVisible'; id: string }
  | { type: 'toggleLocked'; id: string }
  | { type: 'undo' }
  | { type: 'redo' };

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

export const createEditorState = (document: SignDesignDocumentV2): EditorState => ({
  past: [], present: clone(document), future: [],
});

function commit(state: EditorState, present: SignDesignDocumentV2): EditorState {
  return { past: [...state.past, state.present].slice(-50), present, future: [] };
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  if (action.type === 'undo') {
    if (!state.past.length) return state;
    return { past: state.past.slice(0, -1), present: state.past[state.past.length - 1], future: [state.present, ...state.future].slice(0, 50) };
  }
  if (action.type === 'redo') {
    if (!state.future.length) return state;
    return { past: [...state.past, state.present].slice(-50), present: state.future[0], future: state.future.slice(1) };
  }
  let layers = state.present.layers.map(layer => clone(layer));
  if (action.type === 'add') layers.push(clone(action.layer));
  if (action.type === 'remove') layers = layers.filter(layer => layer.id !== action.id);
  if (action.type === 'rename') layers = layers.map(layer => layer.id === action.id ? { ...layer, name: action.name } : layer);
  if (action.type === 'toggleVisible') layers = layers.map(layer => layer.id === action.id ? { ...layer, visible: !layer.visible } : layer);
  if (action.type === 'toggleLocked') layers = layers.map(layer => layer.id === action.id ? { ...layer, locked: !layer.locked } : layer);
  if (action.type === 'group') layers = layers.map(layer => action.ids.includes(layer.id) ? { ...layer, groupId: action.groupId } : layer);
  if (action.type === 'ungroup') layers = layers.map(layer => layer.groupId === action.groupId ? { ...layer, groupId: undefined } : layer);
  if (action.type === 'duplicate') {
    const source = layers.find(layer => layer.id === action.id);
    if (source) layers.push({ ...clone(source), id: action.newId, name: `${source.name} copy`, transform: { ...source.transform, x: source.transform.x + 16, y: source.transform.y + 16 } });
  }
  if (action.type === 'reorder') {
    const current = layers.findIndex(layer => layer.id === action.id);
    if (current >= 0) {
      const [moved] = layers.splice(current, 1);
      layers.splice(Math.max(0, Math.min(action.index, layers.length)), 0, moved);
    }
  }
  return commit(state, { ...state.present, layers });
}

export function scheduleAutosave(document: SignDesignDocumentV2, save: (document: SignDesignDocumentV2) => void, delay = 750): () => void {
  const timer = window.setTimeout(() => save(clone(document)), delay);
  return () => window.clearTimeout(timer);
}

export function useSignDesigner({ initialDocument, autosave }: {
  initialDocument: SignDesignDocumentV2;
  autosave?: (document: SignDesignDocumentV2) => void;
}) {
  const [state, dispatch] = useReducer(editorReducer, initialDocument, createEditorState);
  useEffect(() => autosave ? scheduleAutosave(state.present, autosave) : undefined, [state.present, autosave]);
  const actions = useMemo(() => ({
    add: (layer: SignLayer) => dispatch({ type: 'add', layer }),
    remove: (id: string) => dispatch({ type: 'remove', id }),
    duplicate: (id: string, newId = `${id}-${Date.now()}`) => dispatch({ type: 'duplicate', id, newId }),
    rename: (id: string, name: string) => dispatch({ type: 'rename', id, name }),
    reorder: (id: string, index: number) => dispatch({ type: 'reorder', id, index }),
    group: (ids: string[], groupId: string) => dispatch({ type: 'group', ids, groupId }),
    ungroup: (groupId: string) => dispatch({ type: 'ungroup', groupId }),
    toggleVisible: (id: string) => dispatch({ type: 'toggleVisible', id }),
    toggleLocked: (id: string) => dispatch({ type: 'toggleLocked', id }),
    undo: () => dispatch({ type: 'undo' }),
    redo: () => dispatch({ type: 'redo' }),
  }), []);
  return { document: state.present, canUndo: state.past.length > 0, canRedo: state.future.length > 0, actions };
}
